import { useMutation } from '@apollo/client/react';
import { usePolling } from '@optools/hooks/usePolling';
import {
  ACTIVATE_ADS,
  INGEST_ASSETS_INTO_DAI,
  PAUSE_ADS,
  UPDATE_ASSETS_STATE,
} from '@optools/services/operator-gateway/btc/mutations';
import { BTC_GAME_CONTENT_WITH_ASSETS } from '@optools/services/operator-gateway/btc/queries';
import {
  BtcGameAsset,
  BtcGameContentWithAssets,
} from '@optools/services/operator-gateway/btc/types';
import { AssetState } from '@optools/services/operator-gateway/types/generated/graphql';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { AdStatus } from '../components/ad-status-switch';

const findContentByMediaId = (content: BtcGameContentWithAssets[] | undefined, mediaId: string) => {
  return content?.find((content) => content.mediaId === mediaId);
};

export interface UseAssetMutationsOptions {
  gamePk: number;
  mediaId: string;
}

export function useAssetMutations({ gamePk, mediaId }: UseAssetMutationsOptions) {
  const { date } = useSearch({ from: '/_auth/btc-tools/schedule' });
  const { btcTools } = useAppSelector(selectToolsConfig);

  const {
    adStatusPollingInterval,
    adStatusPollingMaxAttempts,
    assetStatePollingInterval,
    assetStatePollingMaxAttempts,
    ingestIntoDaiPollingInterval,
    ingestIntoDaiPollingMaxAttempts,
  } = btcTools.polling;

  const [pendingAssetId, setPendingAssetId] = useState<number | null>(null);

  const expectedAdStatusRef = useRef<AdStatus | null>(null);
  const expectedAssetStateRef = useRef<AssetState | null>(null);

  // --- Ad Status ---

  const [activateAds, { loading: activateAdsLoading }] = useMutation(ACTIVATE_ADS, {
    onCompleted: () => startAdStatusPolling(),
    onError: () => setPendingAssetId(null),
  });

  const [pauseAds, { loading: pauseAdsLoading }] = useMutation(PAUSE_ADS, {
    onCompleted: () => startAdStatusPolling(),
    onError: () => setPendingAssetId(null),
  });

  const [ingestAssetsIntoDai, { loading: ingestLoading }] = useMutation(INGEST_ASSETS_INTO_DAI, {
    onCompleted: () => startIngestPolling(),
    onError: () => {
      setPendingAssetId(null);
      toast.error('Failed to ingest assets into DAI', { duration: Infinity });
    },
  });

  const handleMaxAttemptsReached = () => {
    toast.warning('Failed to synchronize asset ad status', {
      description: 'Please refresh the assets manually via the "Refresh Media" button.',
      duration: Infinity,
    });
  };

  const { isPolling: isAdStatusPolling, startPolling: startAdStatusPolling } = usePolling({
    interval: adStatusPollingInterval,
    maxAttempts: adStatusPollingMaxAttempts,
    onMaxAttemptsReached: () => {
      handleMaxAttemptsReached();
      setPendingAssetId(null);
    },
    onPollingCompleted: () => setPendingAssetId(null),
    query: BTC_GAME_CONTENT_WITH_ASSETS,
    shouldStopPolling: ({ game }) => {
      const content = findContentByMediaId(game?.content, mediaId);
      if (!content) return false;

      return content.assets
        .filter((asset) => asset.assetId === pendingAssetId)
        .every((asset) => asset.adStatus === expectedAdStatusRef.current);
    },
    variables: { gamePk, scheduleDate: date },
  });

  const { isPolling: isIngestPolling, startPolling: startIngestPolling } = usePolling({
    interval: ingestIntoDaiPollingInterval,
    maxAttempts: ingestIntoDaiPollingMaxAttempts,
    onMaxAttemptsReached: () => {
      handleMaxAttemptsReached();
      setPendingAssetId(null);
    },
    onPollingCompleted: () => setPendingAssetId(null),
    query: BTC_GAME_CONTENT_WITH_ASSETS,
    shouldStopPolling: ({ game }) => {
      const content = findContentByMediaId(game?.content, mediaId);
      if (!content) return false;

      return content.assets
        .filter((asset) => asset.assetId === pendingAssetId)
        .every((asset) => !!asset.adStatus);
    },
    variables: { gamePk, scheduleDate: date },
  });

  // --- Asset State ---

  const [updateAssetsState, { loading: updateAssetsStateLoading }] = useMutation(
    UPDATE_ASSETS_STATE,
    {
      onCompleted: ({ updateAssetsState }) => {
        const [{ reason, success }] = updateAssetsState;
        if (!success) {
          toast.error('Failed to update asset state', { description: reason, duration: Infinity });
          setPendingAssetId(null);
        } else {
          startStatePolling();
        }
      },
      onError: () => setPendingAssetId(null),
    },
  );

  const { isPolling: isStatePolling, startPolling: startStatePolling } = usePolling({
    interval: assetStatePollingInterval,
    maxAttempts: assetStatePollingMaxAttempts,
    onMaxAttemptsReached: () => {
      toast.warning('Failed to synchronize asset state', {
        description: 'Please refresh the assets manually via the "Refresh Media" button.',
        duration: Infinity,
      });
      setPendingAssetId(null);
    },
    onPollingCompleted: () => setPendingAssetId(null),
    query: BTC_GAME_CONTENT_WITH_ASSETS,
    shouldStopPolling: ({ game }) => {
      const content = findContentByMediaId(game?.content, mediaId);
      if (!content) return false;

      return content.assets
        .filter((asset) => asset.assetId === pendingAssetId)
        .every((asset) => asset.state === expectedAssetStateRef.current);
    },
    variables: { gamePk, scheduleDate: date },
  });

  // --- Handlers ---

  const handleToggleAdStatus = useCallback(
    (asset: BtcGameAsset, checked: boolean) => {
      const { assetId, videoId } = asset;
      expectedAdStatusRef.current = checked ? AdStatus.ACTIVE : AdStatus.ADS_PAUSED;
      setPendingAssetId(assetId ?? null);
      void (checked
        ? activateAds({ variables: { mediaId, videoId: videoId ?? '' } })
        : pauseAds({ variables: { mediaId, videoId: videoId ?? '' } }));
    },
    [activateAds, mediaId, pauseAds],
  );

  const handleIngestIntoDai = useCallback(
    (asset: BtcGameAsset) => {
      const { assetId } = asset;
      setPendingAssetId(assetId ?? null);
      ingestAssetsIntoDai({ variables: { assetIds: [assetId ?? 0] } });
    },
    [ingestAssetsIntoDai],
  );

  const handleStateChange = useCallback(
    (asset: BtcGameAsset, state: AssetState) => {
      const { assetId } = asset;
      expectedAssetStateRef.current = state;
      setPendingAssetId(assetId ?? null);
      updateAssetsState({
        variables: { assetState: state, mediaAssets: { assetIds: [assetId ?? 0], mediaId } },
      });
    },
    [mediaId, updateAssetsState],
  );

  const isAdStatusPending =
    activateAdsLoading || pauseAdsLoading || isAdStatusPolling || ingestLoading || isIngestPolling;
  const isStatePending = updateAssetsStateLoading || isStatePolling;

  return {
    handleIngestIntoDai,
    handleStateChange,
    handleToggleAdStatus,
    isAdStatusPending,
    isStatePending,
    pendingAssetId,
  };
}
